import * as THREE from 'three';
import { roomDisplayName } from '@shared-lib/rooms/roomDisplayName';
import { wallFaceSpanY } from '@shared-lib/geometry/wallFace';
import { wallOutlineEnds } from '@shared-lib/geometry/wallOutlineEnds';
import { getWallNormalFromPolygonCorners } from './wallGeometryUtils';

/**
 * What the elevations call a room — its tab, its title, the room line on a
 * wall: the name the way a person writes it ("livingRoom" → "Living room"),
 * else "Room N" by its place in the scan.
 */
export const elevationRoomName = (room, index) =>
  roomDisplayName(room?.name) ?? `Room ${index + 1}`;

// Wall type detection helpers
export const isPolygonWall = (wall) => {
  return wall.polygonCorners && wall.polygonCorners.length >= 3;
};

export const isRectangularWall = (wall) => {
  return wall.center && wall.dimensions && !wall.polygonCorners;
};

// Polygon wall helpers
// A wall's local +Z under its `rotation` — the side it faces by convention
// (the inward normal, CLAUDE.md "Geometry"). Null with no rotation.
const getRotationNormal = (rotation) => {
  if (!rotation) return null;
  let rotX = 0,
    rotY = 0,
    rotZ = 0;
  if (Array.isArray(rotation)) {
    [rotX, rotY, rotZ] = rotation;
  } else if (typeof rotation === 'object') {
    rotX = rotation.x || 0;
    rotY = rotation.y || 0;
    rotZ = rotation.z || 0;
  }
  return new THREE.Vector3(0, 0, 1).applyEuler(
    new THREE.Euler(rotX, rotY, rotZ)
  );
};

// The facing of a polygon wall: the LINE from its whole outline, the SIDE
// from its `rotation`.
//
// The line is read over every edge (Newell's method,
// `getWallNormalFromPolygonCorners`). The old first-two-edges cross product
// fell back to +Z wherever the first three corners lie in a line — the
// junction points along the bottom of 744 of the corpus's 1,119 structure
// walls.
//
// The side is NOT the outline's winding. Every outline processing builds is
// wound to agree with `rotation` (2,297 of 2,297 room walls), but a room
// that keeps RoomPlan's own walls keeps RoomPlan's outline, listed clockwise
// from the top-left — the other way. Read off the winding, those walls face
// out of their room and their elevation draws mirrored (the Master wall on
// DFA5D699, two Room 6 walls on 0D94EEFC). With no rotation, the winding
// is all there is. Horizontal: a wall stands upright.
export const getPolygonWallNormal = (polygonCorners, rotation = null) => {
  const normal = getWallNormalFromPolygonCorners(polygonCorners);
  const side = getRotationNormal(rotation);
  if (!normal) {
    console.warn(
      '[getPolygonWallNormal] Outline has no facing (fewer than 3 corners, or all in one line), returning default normal (0,0,1)'
    );
    return new THREE.Vector3(0, 0, 1);
  }
  if (side && normal.x * side.x + normal.z * side.z < 0) normal.negate();
  return normal;
};

export const getPolygonWallCenter = (polygonCorners) => {
  const center = new THREE.Vector3();
  polygonCorners.forEach((corner) => {
    center.add(new THREE.Vector3(corner.x, corner.y || 0, corner.z));
  });
  return center.divideScalar(polygonCorners.length);
};

export const getPolygonWallBounds = (polygonCorners) => {
  if (!polygonCorners || polygonCorners.length < 3) {
    console.error(
      'getPolygonWallBounds: Missing or invalid polygonCorners (need at least 3)'
    );
    return null;
  }

  const xs = polygonCorners.map((c) => c.x);
  const ys = polygonCorners.map((c) => c.y || 0);
  const zs = polygonCorners.map((c) => c.z);

  // Calculate the actual wall dimensions based on the predominant plane
  const xRange = Math.max(...xs) - Math.min(...xs);
  const yRange = Math.max(...ys) - Math.min(...ys);
  const zRange = Math.max(...zs) - Math.min(...zs);

  // For vertical walls, width is typically the horizontal extent
  // and height is the vertical (y) extent
  let width, height;
  if (yRange > 0.1) {
    // Vertical wall
    width = Math.max(xRange, zRange);
    height = yRange;
  } else {
    // Horizontal wall or floor/ceiling
    width = xRange;
    height = zRange;
  }

  return {
    min: { x: Math.min(...xs), y: Math.min(...ys), z: Math.min(...zs) },
    max: { x: Math.max(...xs), y: Math.max(...ys), z: Math.max(...zs) },
    width: width || 3,
    height: height || 2.5,
    depth: Math.min(xRange, yRange, zRange) || 0.1,
  };
};

// `rotation` is the wall's, so the drawn outline and the objects laid out on
// it read the same facing (`getWallNormal`). Pass it.
export const projectPolygonTo2D = (
  polygonCorners,
  scale = 1,
  rotation = null
) => {
  if (!polygonCorners || polygonCorners.length < 3) return [];

  const normal = getPolygonWallNormal(polygonCorners, rotation);
  const center = getPolygonWallCenter(polygonCorners);

  // Create basis vectors for the wall plane
  const v0 = new THREE.Vector3(
    polygonCorners[0].x,
    polygonCorners[0].y || 0,
    polygonCorners[0].z
  );
  const v1 = new THREE.Vector3(
    polygonCorners[1].x,
    polygonCorners[1].y || 0,
    polygonCorners[1].z
  );

  // Determine the right and up vectors for the wall plane
  let right, up;

  // Check if wall is mostly horizontal
  if (Math.abs(normal.y) > 0.99) {
    // Horizontal wall
    right = new THREE.Vector3(1, 0, 0);
    up = new THREE.Vector3(0, 0, 1);
  } else {
    // Vertical or slanted wall
    const edge1 = v1.clone().sub(v0).normalize();

    // For vertical walls, we want Y to be up
    const worldUp = new THREE.Vector3(0, 1, 0);

    // Right vector is perpendicular to normal and up
    right = new THREE.Vector3().crossVectors(worldUp, normal).normalize();

    // If right is zero (wall is horizontal), use edge as right
    if (right.length() < 0.01) {
      right = edge1;
    }

    // Up vector is perpendicular to normal and right
    up = new THREE.Vector3().crossVectors(normal, right).normalize();
  }

  // Project corners to 2D
  const corners2D = polygonCorners.map((corner) => {
    const p3d = new THREE.Vector3(corner.x, corner.y || 0, corner.z);
    const relative = p3d.sub(center);
    return {
      x: relative.dot(right) * scale,
      y: relative.dot(up) * scale,
    };
  });

  // Normalize coordinates
  const minX = Math.min(...corners2D.map((c) => c.x));
  const minY = Math.min(...corners2D.map((c) => c.y));

  // Shift so minimum x and y are at 0
  corners2D.forEach((c) => {
    c.x -= minX;
    c.y -= minY;
  });

  return corners2D;
};

// Wall normal and position helpers (handles both polygon and rectangular walls)
export const getWallNormal = (wall) => {
  // Check if this is a polygon wall
  if (isPolygonWall(wall)) {
    return getPolygonWallNormal(wall.polygonCorners, wall.rotation);
  }

  // Original rectangular wall logic: local +Z under its rotation.
  if (!wall.rotation) {
    console.warn('Wall has no rotation, using default normal:', wall.id);
    return new THREE.Vector3(0, 0, 1);
  }
  return getRotationNormal(wall.rotation);
};

/**
 * The world direction the elevation renderer lays along screen-X for a
 * wall — the basis every object on that wall is projected with, and the
 * one the movement arrows must share or they point the wrong way. Mirrors
 * the per-wall branches of `RenderWallElevation`; a GROUPED wall carries
 * its own `groupReferenceRight`, which callers must prefer.
 *
 * @param {Object} wall
 * @returns {THREE.Vector3} unit vector
 */
export const getWallRightVector = (wall) => {
  if (isPolygonWall(wall)) {
    const normal = getWallNormal(wall);
    if (Math.abs(normal.y) > 0.99) return new THREE.Vector3(1, 0, 0);
    const right = new THREE.Vector3()
      .crossVectors(new THREE.Vector3(0, 1, 0), normal)
      .normalize();
    if (right.length() >= 0.01) return right;
    const [c0, c1] = wall.polygonCorners;
    return new THREE.Vector3(
      c1.x - c0.x,
      (c1.y || 0) - (c0.y || 0),
      c1.z - c0.z
    ).normalize();
  }
  let rotX = 0,
    rotY = 0,
    rotZ = 0;
  if (Array.isArray(wall?.rotation)) {
    [rotX, rotY, rotZ] = wall.rotation;
  } else if (wall?.rotation && typeof wall.rotation === 'object') {
    rotX = wall.rotation.x || 0;
    rotY = wall.rotation.y || 0;
    rotZ = wall.rotation.z || 0;
  }
  // Auto-detect degrees, as the renderer does.
  if (Math.max(Math.abs(rotX), Math.abs(rotY), Math.abs(rotZ)) > 2 * Math.PI) {
    rotX = THREE.MathUtils.degToRad(rotX);
    rotY = THREE.MathUtils.degToRad(rotY);
    rotZ = THREE.MathUtils.degToRad(rotZ);
  }
  return new THREE.Vector3(1, 0, 0).applyEuler(
    new THREE.Euler(rotX, rotY, rotZ)
  );
};

export const getWallPosition = (wall) => {
  // Prefer the stored rectangle/bbox center when present. For polygon
  // walls this is the polygon's bounding-box center, which is what the
  // elevation renderer uses to size segments (`wall.dimensions.x/y`).
  // The polygon CENTROID drifts away from the bbox center on asymmetric
  // polygons (e.g. L-shaped walls), and using it for object projection
  // shifts openings off the wall by the centroid-vs-bbox delta.
  if (wall.center) {
    if (Array.isArray(wall.center)) {
      return new THREE.Vector3(wall.center[0], wall.center[1], wall.center[2]);
    } else if (typeof wall.center === 'object') {
      return new THREE.Vector3(
        wall.center.x || 0,
        wall.center.y || 0,
        wall.center.z || 0
      );
    }
  }

  // Polygon wall with no stored center — fall back to centroid.
  if (isPolygonWall(wall)) {
    return getPolygonWallCenter(wall.polygonCorners);
  }

  console.warn(
    '[getWallPosition] Wall missing center, returning origin. Wall ID:',
    wall?.id
  );
  return new THREE.Vector3(0, 0, 0);
};

/**
 * Where up the wall an elevation's drawn face is centred, as a distance along
 * `wallUp` — the point the drawing stands at its segment's middle.
 *
 * A segment is drawn from its floor line with the DRAWN face's height (the
 * outline's span on a polygon wall), so the point at its middle is the face's
 * middle, not the stored box's centre (`getWallPosition`). The two differ on a
 * wall whose outline is not its box — a top that follows a raised ceiling —
 * and reading heights from the centre drew everything on it that far off its
 * floor: Cabin's sink wall (face 93.41", box 91.03") stood its cabinets 1.19"
 * high, so a backsplash at its true 36-54" left a strip of wall under the
 * uppers (2026-10-26). On a wall standing on world up this is the face's
 * middle (`wallFaceSpanY`, the frame the finishes are drawn in); on one lying
 * flat, the centre as before.
 *
 * @param {Object} wall
 * @param {THREE.Vector3} wallUp   the drawing's up on this wall
 * @param {THREE.Vector3} wallPos  `getWallPosition(wall)`
 * @returns {number}
 */
export const drawnFaceMidAlongUp = (wall, wallUp, wallPos) => {
  const atCentre = wallPos.dot(wallUp);
  if (Math.abs(wallUp.y) < 0.99) return atCentre;
  const span = wallFaceSpanY(wall);
  if (!span) return atCentre;
  return ((span.bottom + span.top) / 2) * Math.sign(wallUp.y);
};

// Dimension rotation helper
// This accounts for how the object was rotated when the green dot was placed at the back
export const applyRotationToDimensions = (width, height, depth, rotation) => {
  if (!rotation) return { width, height, depth };

  // Rotation is stored as degrees in the GLB object
  const rotX = rotation.x || 0;
  const rotY = rotation.y || 0;
  const rotZ = rotation.z || 0;

  // For simplicity, we'll handle the most common case: Y-axis rotation
  // When user rotates around Y axis to place green dot at back:
  // - 0°: no change
  // - 90°: width becomes depth, depth becomes negative width
  // - 180°: width becomes negative width, depth becomes negative depth
  // - 270°: width becomes negative depth, depth becomes width

  let newWidth = width;
  let newHeight = height;
  let newDepth = depth;

  // Normalize Y rotation to 0-360 range
  const normalizedY = ((rotY % 360) + 360) % 360;

  // Apply Y rotation to width/depth (height stays the same for Y rotation)
  if (Math.abs(normalizedY - 90) < 1) {
    // 90 degree rotation
    newWidth = depth;
    newDepth = width;
  } else if (Math.abs(normalizedY - 180) < 1) {
    // 180 degree rotation
    newWidth = width;
    newDepth = depth;
  } else if (Math.abs(normalizedY - 270) < 1) {
    // 270 degree rotation
    newWidth = depth;
    newDepth = width;
  }

  // Handle X rotation (affects height/depth)
  const normalizedX = ((rotX % 360) + 360) % 360;
  if (Math.abs(normalizedX - 90) < 1 || Math.abs(normalizedX - 270) < 1) {
    // Swap height and depth for 90 or 270 degree X rotation
    const tempHeight = newHeight;
    newHeight = newDepth;
    newDepth = tempHeight;
  }

  return {
    width: Math.abs(newWidth),
    height: Math.abs(newHeight),
    depth: Math.abs(newDepth),
  };
};

// Projection view helper - determines which projection view to use based on wall normal and object rotation
export const getProjectionView = (wallNormal, objRotation) => {
  // Normalize the wall normal
  const normal = wallNormal.clone().normalize();

  // Get the object's local axes after rotation
  const objForward = new THREE.Vector3(0, 0, -1).applyEuler(objRotation); // Object looks down -Z
  const objRight = new THREE.Vector3(1, 0, 0).applyEuler(objRotation);
  const objUp = new THREE.Vector3(0, 1, 0).applyEuler(objRotation);

  // The wall normal points INTO the room (from the wall surface)
  // We view the object from the wall's perspective
  // So we need to determine which face of the object is facing the wall
  const viewDirection = normal.clone().negate(); // Direction we're looking from (opposite of wall normal)

  // Calculate dot products to determine which side of the object faces us
  const forwardDot = Math.abs(viewDirection.dot(objForward));
  const rightDot = Math.abs(viewDirection.dot(objRight));
  const upDot = Math.abs(viewDirection.dot(objUp));

  // Find which axis is most aligned with our viewing direction
  if (forwardDot > rightDot && forwardDot > upDot) {
    // We're looking along the object's forward/back axis
    if (viewDirection.dot(objForward) > 0) {
      // We're looking in the same direction as object forward, so we see the front
      return 'front';
    } else {
      // We're looking opposite to object forward, so we see the back
      return 'back';
    }
  } else if (rightDot > upDot) {
    // We're looking along the object's left/right axis
    if (viewDirection.dot(objRight) > 0) {
      // We're looking from the right, so we see the right side
      return 'right';
    } else {
      // We're looking from the left, so we see the left side
      return 'left';
    }
  } else {
    // We're looking along the object's up/down axis
    if (viewDirection.dot(objUp) > 0) {
      // We're looking from above
      return 'bottom';
    } else {
      // We're looking from below
      return 'top';
    }
  }
};

// Wall endpoint calculation helper (handles both types)
export const getWallEndpoints = (wall) => {
  // For polygon walls, use horizontal bottom edge endpoints
  // Polygon corner ordering (counter-clockwise from front):
  //   [3]───────[2]   (top-left, top-right)
  //    │         │
  //    │  WALL   │
  //    │         │
  //   [0]───────[1]   (bottom-left, bottom-right)
  if (isPolygonWall(wall)) {
    const corners = wall.polygonCorners;

    // Priority 1: Use alignment data if available (most reliable)
    if (wall.alignment?.bottomLeft && wall.alignment?.bottomRight) {
      const endpoint1 = new THREE.Vector3(
        wall.alignment.bottomLeft.x,
        wall.alignment.bottomLeft.y || 0,
        wall.alignment.bottomLeft.z
      );
      const endpoint2 = new THREE.Vector3(
        wall.alignment.bottomRight.x,
        wall.alignment.bottomRight.y || 0,
        wall.alignment.bottomRight.z
      );
      return { endpoint1, endpoint2 };
    }

    // Priority 2: the outline's two ends, each at the foot of the wall. Not
    // "the first two corners" or "the two lowest": a structure wall has a
    // corner at every junction and a room wall can step (`wallOutlineEnds`).
    // Ordered left to right as the renderer lays the wall out, like
    // Priority 1 — outline order is not that: some scanned outlines are
    // stored top edge first, which reverses it.
    const ends = wallOutlineEnds(corners);
    if (ends) {
      const right = getWallRightVector(wall);
      const [a, b] =
        (ends[1].x - ends[0].x) * right.x + (ends[1].z - ends[0].z) * right.z <
        0
          ? [ends[1], ends[0]]
          : ends;
      return {
        endpoint1: new THREE.Vector3(a.x, a.y || 0, a.z),
        endpoint2: new THREE.Vector3(b.x, b.y || 0, b.z),
      };
    }
  }

  // Original rectangular wall logic
  const pos = getWallPosition(wall);

  // Get wall dimensions
  let width = null;
  if (
    wall.dimensions &&
    typeof wall.dimensions === 'object' &&
    !Array.isArray(wall.dimensions)
  ) {
    width = wall.dimensions.x;
  } else if (Array.isArray(wall.dimensions)) {
    width = wall.dimensions[0];
  }

  if (!width) {
    console.warn(
      '[getWallEndpoints] Wall missing dimensions, using fallback 3m. Wall ID:',
      wall?.id
    );
    width = 3;
  }

  // Get wall rotation to find right vector (values expected in radians)
  let wallRotX = 0,
    wallRotY = 0,
    wallRotZ = 0;
  if (wall.rotation) {
    if (Array.isArray(wall.rotation)) {
      [wallRotX, wallRotY, wallRotZ] = wall.rotation;
    } else if (typeof wall.rotation === 'object') {
      wallRotX = wall.rotation.x || 0;
      wallRotY = wall.rotation.y || 0;
      wallRotZ = wall.rotation.z || 0;
    }
  }

  const wallRotation = new THREE.Euler(wallRotX, wallRotY, wallRotZ);
  const wallRight = new THREE.Vector3(1, 0, 0).applyEuler(wallRotation);

  // Calculate endpoints
  const halfWidth = width / 2;
  const endpoint1 = pos
    .clone()
    .add(wallRight.clone().multiplyScalar(-halfWidth));
  const endpoint2 = pos
    .clone()
    .add(wallRight.clone().multiplyScalar(halfWidth));

  return { endpoint1, endpoint2 };
};

// Wall adjacency checker - checks if two walls have close endpoints
// AND are in the same viewable plane (coplanar from viewer's perspective)
export const areWallsAdjacent = (wall1, wall2, threshold) => {
  // Get bottom edge endpoints for both walls (works for polygon and rectangular)
  const { endpoint1: w1e1, endpoint2: w1e2 } = getWallEndpoints(wall1);
  const { endpoint1: w2e1, endpoint2: w2e2 } = getWallEndpoints(wall2);

  // Check only cross-edge combinations to prevent overlapping walls from grouping
  // This ensures walls are side-by-side, not front-to-back
  const minDistance = Math.min(
    w1e1.distanceTo(w2e2), // Wall1-left to Wall2-right
    w1e2.distanceTo(w2e1) // Wall1-right to Wall2-left
  );

  const endpointsAreClose = minDistance <= threshold;

  // If endpoints aren't close, walls aren't adjacent
  if (!endpointsAreClose) return false;

  // Second check: walls must be in the same viewable plane
  // Calculate offset between wall centers
  const pos1 = getWallPosition(wall1);
  const pos2 = getWallPosition(wall2);
  const offset = pos2.clone().sub(pos1);

  // Get wall normal (perpendicular to wall surface, pointing outward)
  const normal1 = getWallNormal(wall1).normalize();

  // Project offset onto the normal direction (perpendicular to wall)
  // This is the component of offset that's invisible when viewing the wall
  const offsetAlongNormal = Math.abs(offset.dot(normal1));

  // If the perpendicular offset is > 0.5m, walls are in different planes
  // and should not be grouped (e.g., L-shaped alcove)
  const PLANE_OFFSET_THRESHOLD = 0.5; // meters

  return offsetAlongNormal < PLANE_OFFSET_THRESHOLD;
};

// Wall grouping helper - groups walls that are close and have same normal
export const groupWalls = (walls) => {
  if (walls.length === 0) return [];

  const groups = [];
  const processed = new Set();
  const DISTANCE_THRESHOLD = 1.524; // 5 feet in meters
  const NORMAL_DOT_THRESHOLD = 0.999; // Very close to parallel (cosine of ~2.5 degrees)

  for (let i = 0; i < walls.length; i++) {
    if (processed.has(i)) continue;

    const wall1 = walls[i];
    const wall1Normal = getWallNormal(wall1).normalize();

    const group = {
      walls: [wall1],
      indices: [i],
      combinedWall: null,
    };

    // Find other walls that can be grouped with this one
    for (let j = i + 1; j < walls.length; j++) {
      if (processed.has(j)) continue;

      const wall2 = walls[j];
      const wall2Normal = getWallNormal(wall2).normalize();

      // Check if normals are similar (pointing in the same direction, not opposite)
      const dotProduct = wall1Normal.dot(wall2Normal);
      if (dotProduct < NORMAL_DOT_THRESHOLD) continue;

      // Check if wall2 is adjacent to ANY wall already in the group
      let isAdjacent = false;
      for (const groupWall of group.walls) {
        if (areWallsAdjacent(groupWall, wall2, DISTANCE_THRESHOLD)) {
          isAdjacent = true;
          break;
        }
      }

      if (!isAdjacent) continue;

      group.walls.push(wall2);
      group.indices.push(j);
      processed.add(j);
    }

    // Mark this wall as processed
    processed.add(i);

    // Create grouped wall representation
    if (group.walls.length > 1) {
      // Sort walls by their position along the wall line for consistent ordering
      // Use the first wall's coordinate system as reference for the entire group
      const referenceWall = group.walls[0];
      const referenceNormal = getWallNormal(referenceWall).normalize();

      // Calculate the reference "right" vector (perpendicular to normal, parallel to wall)
      const worldUp = new THREE.Vector3(0, 1, 0);
      let referenceRight = new THREE.Vector3()
        .crossVectors(worldUp, referenceNormal)
        .normalize();

      // If the wall is horizontal (normal points up/down), use a different approach
      if (referenceRight.length() < 0.01) {
        referenceRight = new THREE.Vector3(1, 0, 0);
      }

      // Sort walls by projecting their centers onto the reference right vector
      const sortedWalls = [...group.walls].sort((a, b) => {
        const posA = getWallPosition(a);
        const posB = getWallPosition(b);

        // Project positions onto the reference right vector
        const projectionA = posA.dot(referenceRight);
        const projectionB = posB.dot(referenceRight);

        return projectionA - projectionB;
      });

      group.combinedWall = {
        ...sortedWalls[0], // Use first wall as base for properties like rotation
        id: `grouped_${sortedWalls.map((w) => w.id || '').join('_')}`,
        roomName: `${sortedWalls[0].roomName} (${sortedWalls.length} walls grouped)`,
        isGrouped: true,
        wallCount: sortedWalls.length,
        groupedWalls: sortedWalls, // Keep individual walls for rendering
        groupReferenceRight: referenceRight, // Store the unified coordinate system
        groupReferenceNormal: referenceNormal,
      };
    } else {
      group.combinedWall = group.walls[0];
    }

    groups.push(group);
  }

  return groups.map((g) => g.combinedWall);
};
